import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { CommonUtilityLib, FilterFormDataModel, FilterTab, TabMenuEvent } from '@thehubz/client/core';
import { AnyType } from '@thehubz/shared/model';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { NavigationTabService } from './navigation-tab.service';

@Component({
  selector: 'sp-navigation-tab-link',
  templateUrl: './navigation-tab-link.component.html',
  styleUrls: ['./navigation-tab-link.component.scss'],
})
export class NavigationTabLinkComponent implements AfterViewInit {
  @Input() filterTabs!: FilterTab[];
  @Input() selectedTabKey!: string;
  @Input() filterProjectFormData!: FilterFormDataModel;
  @Input() dialogDataConfig!: { filterType: string; usersFieldType: string };

  @Output() selectedTab = new EventEmitter<TabMenuEvent>();
  @Output() dialogResult = new EventEmitter<AnyType>();

  @ViewChild('tabComponent') tabComponent?: ElementRef;
  @ViewChild('renameInput') renameInput?: ElementRef;
  @ViewChild('menuButton') menuButton?: ElementRef;

  tabName = '';
  toggleEditTab = false;
  currentTab = '';
  editingTab = '';
  isHidden = false;
  paginationDirection = '';

  constructor(private commonUtilityLib: CommonUtilityLib, private confirmDialog: ConfirmDialogService, private navigationTabService: NavigationTabService) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      const tabListEl = this.tabComponent?.nativeElement?.querySelector('.mat-mdc-tab-list');

      if (tabListEl) {
        tabListEl.addEventListener('transitionend', (event: any) => {
          this.setPaginationDirection();
        });

        this.setPaginationDirection();
      }
    }, 0);
  }

  /**
   * Method to select tabs
   * @param index
   * @param link
   */
  tabSelected(index: number, link: string, event: any, isCustomTab = false) {
    if (/button|input/i.test(event.target.nodeName)) {
      return;
    }
    // check for current tab id and previous tab id selection
    if (this.currentTab !== link) this.toggleEditTab = false;
    this.currentTab = link;
    this.selectedTab.emit({ index, link, isCustomTab });
  }

  /**
   * Add new tabs
   */
  addTabs() {
    const dialogRef = this.navigationTabService.addTabs(this.filterProjectFormData, this.dialogDataConfig);
    dialogRef.subscribe((result) => {
      if (result) {
        this.dialogResult.emit(result);
      }
    });
  }

  /**
   * Rename tabs
   * @param index
   */
  renameTab(index: number) {
    this.toggleEditTab = true;

    const tab = this.filterTabs[index];
    // assign the current name to the tabName input
    this.tabName = tab.text;
    this.editingTab = tab.key;

    setTimeout(() => {
      this.renameInput?.nativeElement?.focus();
      this.renameInput?.nativeElement?.select();
    }, 0);
  }

  /**
   * Exit rename tabs
   * @param index
   */
  exitRenameTab(index: number) {
    this.toggleEditTab = false;
    this.editingTab = '';

    setTimeout(() => {
      this.menuButton?.nativeElement.focus();
    }, 0);
  }

  /**
   * Save modified tag name
   * @param index
   */
  saveTabName(index: number) {
    const nameCamelCase = this.commonUtilityLib.toCamelCase(this.tabName);
    const filterObject: FilterTab = {
      key: nameCamelCase,
      text: this.tabName,
      visible: true,
      filterId: nameCamelCase + '_' + index,
      selectKey: nameCamelCase + '_' + index,
      tabMenu: true,
      tabType: 'dynamic',
    };
    const previousTabInfo = this.filterTabs[index];
    this.filterTabs[index] = filterObject;
    this.selectedTab.emit({ index, link: filterObject.selectKey, actionType: 'rename', previousTabInfo });
    this.toggleEditTab = false;
  }

  /**
   * Pin the tabs to front
   * @param index
   */
  pinTabToFront(index: number) {
    const deletedTab: FilterTab = Object.assign({}, ...this.filterTabs.splice(index, 1));
    const deleteTabFilterId = deletedTab.filterId;
    this.filterTabs.unshift(deletedTab);
    const linkText = deletedTab.key + '_' + 0;
    this.selectedTabKey = deletedTab.filterId;
    this.selectedTab.emit({ index: 0, link: linkText, actionType: 'pinToFront', tabType: deletedTab.tabType, deleteTabFilterId });

    // Fix matTabNav position gets stuck
    setTimeout(() => {
      this.isHidden = true;

      setTimeout(() => {
        this.isHidden = false;
      }, 0);
    }, 0);
  }

  unPinTab() {
    const pinnedTabToMove: FilterTab = Object.assign({}, ...this.filterTabs.splice(0, 1));
    this.filterTabs.push(pinnedTabToMove);
    this.selectedTabKey = pinnedTabToMove.selectKey;
    this.selectedTab.emit({
      index: this.filterTabs.length - 1,
      link: '',
      actionType: 'unpin',
      tabType: pinnedTabToMove.tabType,
      deleteTabFilterId: pinnedTabToMove.selectKey,
    });
  }

  /**
   * Delete tab
   * @param index
   */
  deleteTab(index: number) {
    this.confirmDialog
      .open({
        title: $localize`Delete filter`,
        message: $localize`Are you sure you want to delete this filter?`,
      })
      .subscribe((result) => {
        if (result) {
          const deletedTab = this.filterTabs[index];
          this.filterTabs.splice(index, 1);
          this.selectedTabKey = this.filterTabs[0].selectKey;
          this.selectedTab.emit({ index: 0, link: this.selectedTabKey, actionType: 'delete', previousTabInfo: deletedTab });
        }
      });
  }

  setPaginationDirection() {
    const paginationButtons = this.tabComponent?.nativeElement?.querySelectorAll('.mat-mdc-tab-header-pagination');

    if (!paginationButtons.length) return;

    const disabledPaginationButtons = [...paginationButtons].filter((button: HTMLButtonElement) => button.disabled);

    if (disabledPaginationButtons.length === 0) {
      this.paginationDirection = 'both';
    } else if (disabledPaginationButtons.length === 1) {
      if (disabledPaginationButtons[0].classList.contains('mat-mdc-tab-header-pagination-before')) {
        this.paginationDirection = 'after';
      } else if (disabledPaginationButtons[0].classList.contains('mat-mdc-tab-header-pagination-after')) {
        this.paginationDirection = 'before';
      }
    } else {
      this.paginationDirection = '';
    }
  }
}
